import React, { useEffect, useRef, useState } from 'react';
import './TRexGame.css';

const TRexGame = () => {
  const canvasRef = useRef(null);
  const [score, setScore] = useState(0);
  const [highScore, setHighScore] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const animationFrameRef = useRef(null);
  const gameRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const WIDTH = 900;   // Largeur x1.5 (était 600)
    const HEIGHT = 300;  // Hauteur x1.5 (était 200)

    // Configuration du jeu
    const config = {
      gravity: 0.6,
      jumpVelocity: -12,
      speed: 4,              // Vitesse initiale réduite (était 6)
      maxSpeed: 9,           // Vitesse max réduite (était 13)
      acceleration: 0.0005,  // Accélération plus lente (était 0.001)
      cloudSpeed: 0.15,      // Nuages plus lents (était 0.2)
      groundY: 225           // Ajusté pour hauteur x1.5 (était 150)
    };

    // Classe Dinosaure
    class Dino {
      constructor() {
        this.width = 44;
        this.height = 47;
        this.x = 50;
        this.groundY = config.groundY - this.height;
        this.y = this.groundY;
        this.vy = 0;
        this.jumping = false;
        this.ducking = false;
        this.animFrame = 0;
        this.animTimer = 0;
      }

      jump() {
        if (!this.jumping && !this.ducking) {
          this.vy = config.jumpVelocity;
          this.jumping = true;
        }
      }

      duck(isDucking) {
        if (!this.jumping) {
          this.ducking = isDucking;
        }
      }

      update() {
        // Gravité
        if (this.jumping) {
          this.vy += config.gravity;
          this.y += this.vy;

          if (this.y >= this.groundY) {
            this.y = this.groundY;
            this.vy = 0;
            this.jumping = false;
      }
        }

        // Animation de course
        this.animTimer++;
        if (this.animTimer > 5) {
          this.animFrame = this.animFrame === 0 ? 1 : 0;
          this.animTimer = 0;
        }
      }

      draw(ctx) {
        ctx.fillStyle = '#535353';
        
        if (this.ducking) {
          this.drawDucking(ctx);
        } else {
          this.drawRunning(ctx);
      }
      }

      drawRunning(ctx) {
        const x = this.x;
        const y = this.y;

        // Corps
        ctx.fillRect(x + 20, y, 24, 24);
        // Cou
        ctx.fillRect(x + 32, y - 8, 8, 8);
        // Tête
        ctx.fillRect(x + 32, y - 16, 18, 10);
        // Œil
        ctx.fillStyle = '#fff';
        ctx.fillRect(x + 38, y - 14, 4, 4);
        ctx.fillStyle = '#535353';
        // Bec
        ctx.fillRect(x + 48, y - 13, 4, 4);
        // Bras
        ctx.fillRect(x + 18, y + 10, 8, 14);
        // Queue
        ctx.fillRect(x, y + 10, 20, 8);

        // Jambes (animation)
        if (this.animFrame === 0) {
          ctx.fillRect(x + 20, y + 24, 6, 10);
          ctx.fillRect(x + 22, y + 34, 6, 4);
          ctx.fillRect(x + 32, y + 24, 6, 14);
          ctx.fillRect(x + 34, y + 38, 6, 4);
        } else {
          ctx.fillRect(x + 20, y + 24, 6, 14);
          ctx.fillRect(x + 22, y + 38, 6, 4);
          ctx.fillRect(x + 32, y + 24, 6, 10);
          ctx.fillRect(x + 34, y + 34, 6, 4);
        }
      }

      drawDucking(ctx) {
        const x = this.x;
        const y = this.groundY + 20;

        // Corps
        ctx.fillRect(x + 20, y, 40, 20);
        // Tête
        ctx.fillRect(x + 54, y - 4, 16, 14);
        // Œil
        ctx.fillStyle = '#fff';
        ctx.fillRect(x + 60, y - 1, 4, 4);
        ctx.fillStyle = '#535353';
        // Queue
        ctx.fillRect(x, y + 5, 20, 10);
        // Bras
        ctx.fillRect(x + 18, y + 8, 8, 10);

        // Pattes
        if (this.animFrame === 0) {
          ctx.fillRect(x + 26, y + 18, 6, 4);
          ctx.fillRect(x + 44, y + 18, 6, 4);
        } else {
          ctx.fillRect(x + 30, y + 18, 6, 4);
          ctx.fillRect(x + 48, y + 18, 6, 4);
        }
      }

      getHitbox() {
        if (this.ducking) {
          return {
            x: this.x + 22,
            y: this.groundY + 20,
            width: 54,
            height: 22
          };
        }
        return {
          x: this.x + 18,
          y: this.y + 2,
          width: 36,
          height: 44
        };
      }
    }

    // Classe Obstacle
    class Obstacle {
      constructor(type, speed) {
        this.type = type;
        this.speed = speed;
        this.x = WIDTH + 10;
        
        if (type === 'cactus-small') {
          this.width = 17;
          this.height = 35;
          this.y = config.groundY - this.height;
        } else if (type === 'cactus-large') {
          this.width = 25;
          this.height = 50;
          this.y = config.groundY - this.height;
        } else if (type === 'bird') {
          this.width = 46;
          this.height = 40;
          const heights = [config.groundY - 90, config.groundY - 60, config.groundY - 40];
          this.y = heights[Math.floor(Math.random() * heights.length)];
          this.animFrame = 0;
          this.animTimer = 0;
        }
      }

      update() {
        this.x -= this.speed;
        
        if (this.type === 'bird') {
          this.animTimer++;
          if (this.animTimer > 8) {
            this.animFrame = this.animFrame === 0 ? 1 : 0;
            this.animTimer = 0;
          }
        }
      }

      draw(ctx) {
        ctx.fillStyle = '#535353';
        
        if (this.type === 'cactus-small') {
          this.drawSmallCactus(ctx);
        } else if (this.type === 'cactus-large') {
          this.drawLargeCactus(ctx);
        } else if (this.type === 'bird') {
          this.drawBird(ctx);
        }
      }

      drawSmallCactus(ctx) {
        const x = this.x;
        const y = this.y;
        
        // Tronc principal
        ctx.fillRect(x + 5, y, 6, 35);
        // Bras gauche
        ctx.fillRect(x, y + 10, 6, 12);
        ctx.fillRect(x + 5, y + 10, 3, 3);
        // Bras droit
        ctx.fillRect(x + 11, y + 18, 6, 10);
        ctx.fillRect(x + 11, y + 18, 3, 3);
      }

      drawLargeCactus(ctx) {
        const x = this.x;
        const y = this.y;
        
        // Tronc principal
        ctx.fillRect(x + 8, y, 10, 50);
        // Bras gauche
        ctx.fillRect(x, y + 15, 8, 18);
        ctx.fillRect(x + 7, y + 15, 3, 3);
        // Bras droit
        ctx.fillRect(x + 18, y + 20, 7, 15);
        ctx.fillRect(x + 18, y + 20, 3, 3);
      }

      drawBird(ctx) {
        const x = this.x;
        const y = this.y;
        
        // Corps
        ctx.fillRect(x + 10, y + 15, 26, 15);
        // Tête/Cou
        ctx.fillRect(x + 28, y + 8, 10, 10);
        // Bec
        ctx.fillRect(x + 36, y + 10, 10, 6);
        
        // Ailes (animation)
        if (this.animFrame === 0) {
          ctx.fillRect(x, y + 12, 10, 12);
          ctx.fillRect(x + 36, y + 12, 10, 12);
        } else {
          ctx.fillRect(x + 2, y + 20, 10, 12);
          ctx.fillRect(x + 34, y + 20, 10, 12);
      }

        // Œil
        ctx.fillStyle = '#fff';
        ctx.fillRect(x + 30, y + 10, 3, 3);
      }

      getHitbox() {
        return {
          x: this.x + 4,
          y: this.y + 4,
          width: this.width - 8,
          height: this.height - 8
        };
      }

      isOffScreen() {
        return this.x + this.width < 0;
      }
    }

    // Classe Nuage
    class Cloud {
      constructor() {
        this.width = 46;
        this.height = 14;
        this.x = WIDTH + Math.random() * 100;
        this.y = 20 + Math.random() * 40;
      }

      update() {
        this.x -= config.cloudSpeed;
        if (this.x + this.width < 0) {
          this.x = WIDTH + Math.random() * 100;
          this.y = 20 + Math.random() * 40;
        }
      }

      draw(ctx) {
        ctx.fillStyle = '#c4c4c4';
        ctx.fillRect(this.x + 10, this.y, 26, 8);
        ctx.fillRect(this.x, this.y + 3, 10, 8);
        ctx.fillRect(this.x + 36, this.y + 3, 10, 8);
      }
    }

    // Classe Ground
    class Ground {
      constructor() {
        this.lines = [];
        for (let i = 0; i < 10; i++) {
          this.lines.push({
            x: i * 60,
            y: config.groundY,
            width: 40,
            height: 2
          });
        }
      }

      update(speed) {
        this.lines.forEach(line => {
          line.x -= speed;
          if (line.x + line.width < 0) {
            line.x = WIDTH;
          }
        });
      }

      draw(ctx) {
        // Ligne principale du sol (au niveau des pattes)
        ctx.fillStyle = '#535353';
        ctx.fillRect(0, config.groundY, WIDTH, 2);
        
        // Lignes de détail
        this.lines.forEach(line => {
          ctx.fillRect(line.x, line.y, line.width, line.height);
        });
      }
    }

    // Game Manager
    class Game {
      constructor() {
        this.reset();
      }

      reset() {
        this.dino = new Dino();
        this.obstacles = [];
        this.clouds = [new Cloud(), new Cloud(), new Cloud()];
        this.ground = new Ground();
        this.score = 0;
        this.speed = config.speed;
        this.obstacleTimer = 0;
        this.obstacleInterval = 90;
        this.frameCount = 0;
        this.started = false;
        this.over = false;
      }

      start() {
        this.started = true;
        setGameStarted(true);
        setGameOver(false);
      }

      handleInput(type, value) {
        if (!this.started && type === 'jump') {
          this.start();
        }

        if (this.over && type === 'jump') {
          this.reset();
          this.start();
          setScore(0);
          return;
        }

        if (!this.over) {
          if (type === 'jump') {
            this.dino.jump();
          } else if (type === 'duck') {
            this.dino.duck(value);
          }
        }
      }

      spawnObstacle() {
        const types = ['cactus-small', 'cactus-large'];
        
        // Ajouter des oiseaux après le score 100
        if (this.score > 100) {
          types.push('bird');
        }
        
        const type = types[Math.floor(Math.random() * types.length)];
        this.obstacles.push(new Obstacle(type, this.speed));
      }

      checkCollision() {
        const dinoBox = this.dino.getHitbox();
        
        for (let obstacle of this.obstacles) {
          const obstacleBox = obstacle.getHitbox();
          
          if (dinoBox.x < obstacleBox.x + obstacleBox.width &&
              dinoBox.x + dinoBox.width > obstacleBox.x &&
              dinoBox.y < obstacleBox.y + obstacleBox.height &&
              dinoBox.y + dinoBox.height > obstacleBox.y) {
            return true;
          }
        }
        return false;
      }

      update() {
        if (this.over || !this.started) return;

        this.frameCount++;

        // Mise à jour du dinosaure
        this.dino.update();

        // Mise à jour des nuages
        this.clouds.forEach(cloud => cloud.update());

        // Mise à jour du sol
        this.ground.update(this.speed);

        // Génération d'obstacles
        this.obstacleTimer++;
        if (this.obstacleTimer > this.obstacleInterval) {
          this.spawnObstacle();
          this.obstacleTimer = 0;
          this.obstacleInterval = 50 + Math.random() * 50;
        }

        // Mise à jour des obstacles
        this.obstacles.forEach(obstacle => obstacle.update());

        // Suppression des obstacles hors écran
        this.obstacles = this.obstacles.filter(obstacle => {
          if (obstacle.isOffScreen()) {
            this.score++;
            setScore(this.score);
            return false;
          }
          return true;
        });
          
        // Augmentation de la vitesse
        if (this.frameCount % 10 === 0 && this.speed < config.maxSpeed) {
          this.speed += config.acceleration;
        }

        // Vérification des collisions
        if (this.checkCollision()) {
          this.gameOver();
        }
      }

      gameOver() {
        this.over = true;
        setGameOver(true);
        
        if (this.score > highScore) {
          setHighScore(this.score);
          }
        }

      draw(ctx) {
        // Fond blanc
        ctx.fillStyle = '#fff';
        ctx.fillRect(0, 0, WIDTH, HEIGHT);

        // Nuages
        this.clouds.forEach(cloud => cloud.draw(ctx));

        // Sol
        this.ground.draw(ctx);

        // Obstacles
        this.obstacles.forEach(obstacle => obstacle.draw(ctx));

        // Dinosaure
        this.dino.draw(ctx);

        // Score
        ctx.fillStyle = '#535353';
        ctx.font = 'bold 16px "Courier New", monospace';
        ctx.textAlign = 'right';
        
        if (highScore > 0) {
          ctx.fillText(`HI ${String(Math.floor(highScore)).padStart(5, '0')}`, WIDTH - 110, 25);
        }
        ctx.fillText(String(Math.floor(this.score)).padStart(5, '0'), WIDTH - 15, 25);

        // Message de démarrage
        if (!this.started) {
          ctx.fillStyle = '#535353';
          ctx.font = '14px "Courier New", monospace';
          ctx.textAlign = 'center';
          ctx.fillText('Appuyez sur ESPACE pour commencer', WIDTH / 2, HEIGHT / 2);
        }

        // Game Over
        if (this.over) {
          ctx.fillStyle = '#535353';
          ctx.font = 'bold 24px "Courier New", monospace';
        ctx.textAlign = 'center';
          ctx.fillText('G A M E   O V E R', WIDTH / 2, HEIGHT / 2 - 10);
          ctx.font = '14px "Courier New", monospace';
          ctx.fillText('Appuyez sur ESPACE pour rejouer', WIDTH / 2, HEIGHT / 2 + 20);
        }
      }
    }

    // Initialisation du jeu
    const game = new Game();
    gameRef.current = game;

    // Gestionnaires d'événements
    const handleKeyDown = (e) => {
      if (e.code === 'Space' || e.code === 'ArrowUp') {
        e.preventDefault();
        game.handleInput('jump');
      } else if (e.code === 'ArrowDown') {
        e.preventDefault();
        game.handleInput('duck', true);
      }
    };

    const handleKeyUp = (e) => {
      if (e.code === 'ArrowDown') {
        e.preventDefault();
        game.handleInput('duck', false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    // Boucle de jeu
    const gameLoop = () => {
      game.update();
      game.draw(ctx);
      animationFrameRef.current = requestAnimationFrame(gameLoop);
    };

    gameLoop();

    // Cleanup
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [highScore]);

  return (
    <div className="game-container trex-chrome">
      <h3>🦖 T-Rex Runner</h3>
      <p className="game-instructions">
        <span>ESPACE/↑: Sauter</span>
        <span>↓: Se baisser</span>
      </p>
      <canvas 
        ref={canvasRef} 
        width={900} 
        height={300} 
        className="game-canvas chrome-canvas"
      />
      {!gameStarted && !gameOver && (
        <p className="game-hint">Appuyez sur ESPACE pour commencer</p>
      )}
    </div>
  );
};

export default TRexGame;
